import React, { useRef, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";

export default function ChatWaveform({ isRecording, mediaStream }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const mediaStreamSourceRef = useRef(null);
  const barHeightsHistoryRef = useRef([]); // Ref to store history of bar heights
  const [canvasSize, setCanvasSize] = useState({ width: 500, height: 80 });

  // Responsive canvas sizing
  useEffect(() => {
    const updateCanvasSize = () => {
      if (canvasRef.current) {
        // Use the canvas wrapper as the parent to get accurate width
        const parent = canvasRef.current.parentElement;
        if (!parent) {
          return;
        }

        // Force a reflow to get accurate dimensions
        parent.offsetHeight; // Trigger reflow

        // Get the full width of the parent container
        const rect = parent.getBoundingClientRect();
        const width = rect.width || parent.offsetWidth || parent.clientWidth;
        const height = 80; // Fixed height for consistency



        // Only update if width is valid and different from current
        if (width > 0 && Math.abs(width - canvasSize.width) > 1) {
          setCanvasSize({ width, height });
        }
      }
    };

    // Initial update
    updateCanvasSize();

    // Update on resize
    window.addEventListener('resize', updateCanvasSize);

    // Use ResizeObserver for better container size detection
    let resizeObserver;
    if (canvasRef.current && canvasRef.current.parentElement) {
      resizeObserver = new ResizeObserver(() => {
        updateCanvasSize();
      });
      resizeObserver.observe(canvasRef.current.parentElement);
    }

    // Update after delays to catch late renders
    const timeout1 = setTimeout(updateCanvasSize, 100);
    const timeout2 = setTimeout(updateCanvasSize, 300);
    const timeout3 = setTimeout(updateCanvasSize, 500);

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
    };
  }, [canvasSize.width]);

  // Update canvas size when recording starts
  useEffect(() => {
    if (isRecording && canvasRef.current) {
      const parent = canvasRef.current.parentElement;
      if (parent) {
        const width = parent.getBoundingClientRect().width || parent.offsetWidth || parent.clientWidth;
        const height = 80;
        if (width > 0) {
          setCanvasSize({ width, height });
        }
      }
    }
  }, [isRecording]);

  useEffect(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256; // Reverted FFT size for more detailed audio data
    }
    const audioContext = audioContextRef.current;
    const analyser = analyserRef.current;

    const bufferLength = analyser.frequencyBinCount;
    if (!dataArrayRef.current) {
      dataArrayRef.current = new Uint8Array(bufferLength);
    }

    return () => {
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const audioContext = audioContextRef.current;
    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;
    const canvas = canvasRef.current;
    const canvasCtx = canvas?.getContext('2d');

    const barWidth = 2; // Width of each bar
    const barSpacing = 1; // Space between bars
    const totalBarWidth = barWidth + barSpacing;



    if (!isRecording || !mediaStream || !audioContext || !analyser || !canvasCtx) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (mediaStreamSourceRef.current) {
        mediaStreamSourceRef.current.disconnect();
        mediaStreamSourceRef.current = null;
      }
      // Clear history when not recording
      barHeightsHistoryRef.current = [];
      canvasCtx?.clearRect(0, 0, canvas?.width || 0, canvas?.height || 0);
      return;
    }

    // Disconnect existing source if any and connect new one
    if (mediaStreamSourceRef.current) {
      mediaStreamSourceRef.current.disconnect();
      mediaStreamSourceRef.current = null;
    }

    try {
      mediaStreamSourceRef.current = audioContext.createMediaStreamSource(mediaStream);
      mediaStreamSourceRef.current.connect(analyser);

      if (audioContext.state === 'suspended') {
        audioContext.resume().then(() => {
        }).catch(e => console.error('Error resuming AudioContext for waveform:', e));
      }

      const minDotHeight = 2; // Minimum height for silent bars (dots)
      const peakReductionFactor = 0.5; // Increased amplitude - waves will be twice as tall

      // Helper to get theme color
      const getThemeColor = (opacity) => {
        const computedStyle = getComputedStyle(document.documentElement);
        let primaryColorValue =
          computedStyle.getPropertyValue('--primary').trim() ||
          computedStyle.getPropertyValue('--color-primary').trim();

        if (primaryColorValue) {
          if (primaryColorValue.startsWith('oklch')) {
            return `oklch(${primaryColorValue.split(' ').join(' ')} / ${opacity})`;
          }
        }
        // Fallback to Teal (matches --primary in dark mode roughly)
        return `rgba(52, 208, 148, ${opacity})`;
      };

      const draw = () => {
        animationFrameRef.current = requestAnimationFrame(draw);

        // Calculate numBars dynamically based on current canvas width
        const numBars = Math.floor(canvas.width / totalBarWidth) || 0;

        analyser.getByteTimeDomainData(dataArray);

        // Calculate a single new bar height from the current audio data
        const averageAmplitude = dataArray.reduce((sum, val) => sum + Math.abs(val - 128), 0) / dataArray.length;

        // Scale amplitude to bar height, from minDotHeight to (canvas.height * peakReductionFactor)
        let newBarHeight = minDotHeight + (averageAmplitude / 127) * (canvas.height - minDotHeight) * peakReductionFactor;

        // Ensure barHeight does not exceed the reduced max height and is at least minDotHeight
        newBarHeight = Math.max(minDotHeight, Math.min(newBarHeight, canvas.height * peakReductionFactor));

        // Add new bar height to history and keep history size limited to numBars
        barHeightsHistoryRef.current.push(newBarHeight);
        if (barHeightsHistoryRef.current.length > numBars) {
          barHeightsHistoryRef.current.shift(); // Remove the oldest bar
        }

        canvasCtx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw all bars from history, creating the scrolling effect with modern colors
        barHeightsHistoryRef.current.forEach((barHeight, i) => {
          const y = canvas.height / 2 - barHeight / 2;
          const x = i * totalBarWidth;

          // Gradient from teal to slightly lighter teal based on height
          const normalizedHeight = barHeight / (canvas.height * peakReductionFactor);
          const opacity = 0.6 + (normalizedHeight * 0.4); // Vary opacity 0.6 to 1.0

          canvasCtx.fillStyle = getThemeColor(opacity);
          canvasCtx.fillRect(x, y, barWidth, barHeight);
        });
      };

      draw();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: 'ChatWaveform - audio stream setup',
          isRecording,
          hasMediaStream: !!mediaStream,
          audioContextState: audioContext?.state,
          numBars: Math.floor(canvas.width / totalBarWidth) || 0
        }
      });
      console.error("Error setting up audio stream for waveform:", error);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (mediaStreamSourceRef.current) {
        mediaStreamSourceRef.current.disconnect();
        mediaStreamSourceRef.current = null;
      }
      barHeightsHistoryRef.current = []; // Clear history on error
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (mediaStreamSourceRef.current) {
        mediaStreamSourceRef.current.disconnect();
        mediaStreamSourceRef.current = null;
      }
      barHeightsHistoryRef.current = []; // Clear history on component unmount/re-render
    };
  }, [isRecording, mediaStream, canvasSize]);

  return (
    <canvas
      ref={canvasRef}
      className="chat-waveform "
      width={canvasSize.width}
      height={canvasSize.height}
    ></canvas>
  );
}